import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { RecordFormDialog } from "@taunggyi-plus/design-system";
type Call = {
  id: string;
  caller: string;
  callee: string;
  status: string;
  peer_name: string;
  offer: RTCSessionDescriptionInit;
  answer: RTCSessionDescriptionInit | null;
  caller_ice: RTCIceCandidateInit[];
  callee_ice: RTCIceCandidateInit[];
};
const VoiceContext = createContext<{
  start: (id: string, kind?: "taxi" | "restaurant") => void;
  busy: boolean;
} | null>(null);
export function TaxiCallButton({
  driverId,
  disabled = false,
}: {
  driverId: string;
  disabled?: boolean;
}) {
  const voice = useContext(VoiceContext);
  return (
    <button
      type="button"
      className="button"
      disabled={disabled || !voice || voice.busy}
      onClick={() => voice?.start(driverId)}
    >
      Call in app
    </button>
  );
}
export function RestaurantCallButton({ shopId }: { shopId: string }) {
  const voice = useContext(VoiceContext);
  return (
    <button
      type="button"
      className="button"
      disabled={!voice || voice.busy}
      onClick={() => voice?.start(shopId, "restaurant")}
    >
      Call restaurant
    </button>
  );
}
export function TaxiVoiceProvider({
  children,
  iceServers = [],
}: {
  children: ReactNode;
  iceServers?: RTCIceServer[];
}) {
  const { client, user } = useAuth();
  const [call, setCall] = useState<Call | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [muted, setMuted] = useState(false),
    [connected, setConnected] = useState(false),
    [audioBlocked, setAudioBlocked] = useState(false);
  const current = useRef<Call | null>(null),
    peer = useRef<RTCPeerConnection | null>(null),
    stream = useRef<MediaStream | null>(null),
    audio = useRef<HTMLAudioElement>(null),
    pendingIce = useRef<RTCIceCandidateInit[]>([]),
    seenIce = useRef(0),
    generation = useRef(0),
    lock = useRef(false);
  const config = useRef(iceServers);
  const cleanup = useCallback(() => {
    generation.current++;
    peer.current?.close();
    peer.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (audio.current) {
      audio.current.pause();
      audio.current.srcObject = null;
    }
    pendingIce.current = [];
    seenIce.current = 0;
    setConnected(false);
    setMuted(false);
    setAudioBlocked(false);
  }, []);
  const signal = useCallback(
    async (id: string, action: string, payload: unknown = null) => {
      if (!client) throw new Error("Please sign in again.");
      const r = await client.rpc("signal_taxi_call", { call_id: id, action, payload });
      if (r.error) throw new Error(r.error.message);
    },
    [client],
  );
  const finish = useCallback(
    async (action = "end") => {
      const id = current.current?.id;
      current.current = null;
      setCall(null);
      cleanup();
      setBusy(false);
      if (id)
        try {
          await signal(id, action);
        } catch {
          setMessage(
            "Call stopped on this device. The other app will time out if the connection is lost.",
          );
        }
    },
    [cleanup, signal],
  );
  async function makePeer() {
    if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection)
      throw new Error("Voice calling needs a supported browser and microphone access.");
    const token = generation.current;
    const configuration = await client?.rpc("taxi_voice_config");
    if (configuration?.error) throw new Error(configuration.error.message);
    const servers = configuration?.data?.iceServers as RTCIceServer[] | undefined;
    if (token !== generation.current) throw new Error("Call cancelled.");
    const media = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: false,
    });
    if (token !== generation.current) {
      media.getTracks().forEach((t) => t.stop());
      throw new Error("Call cancelled.");
    }
    stream.current = media;
    const pc = new RTCPeerConnection({ iceServers: servers ?? config.current });
    peer.current = pc;
    media.getTracks().forEach((t) => pc.addTrack(t, media));
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const candidate = e.candidate.toJSON();
        const id = current.current?.id;
        if (id)
          void signal(id, "ice", candidate).catch(() =>
            setMessage("Connection interrupted. End the call and retry."),
          );
        else pendingIce.current.push(candidate);
      }
    };
    pc.ontrack = (e) => {
      if (audio.current) {
        audio.current.srcObject = e.streams[0] ?? new MediaStream([e.track]);
        void audio.current.play().catch(() => setAudioBlocked(true));
      }
    };
    pc.onconnectionstatechange = () => {
      setConnected(pc.connectionState === "connected");
      if (pc.connectionState === "failed") {
        setMessage("Could not connect the call. Please try again.");
        void finish();
      }
    };
    return pc;
  }
  async function start(driverId: string, kind: "taxi" | "restaurant" = "taxi") {
    if (!client || !user || lock.current || current.current) return;
    lock.current = true;
    setBusy(true);
    setMessage("");
    try {
      const pc = await makePeer();
      await pc.setLocalDescription(await pc.createOffer());
      const r = await client.rpc(kind === "taxi" ? "start_taxi_call" : "start_restaurant_call", {
        [kind === "taxi" ? "driver" : "shop"]: driverId,
        sdp: pc.localDescription?.toJSON(),
      });
      if (r.error) throw new Error(r.error.message);
      const id = String(r.data);
      if (pc !== peer.current) {
        await signal(id, "end");
        return;
      }
      const next: Call = {
        id,
        caller: user.id,
        callee: "",
        status: "ringing",
        peer_name: kind === "taxi" ? "Taxi driver" : "Restaurant",
        offer: pc.localDescription!.toJSON(),
        answer: null,
        caller_ice: [],
        callee_ice: [],
      };
      current.current = next;
      setCall(next);
      for (const candidate of pendingIce.current.splice(0)) await signal(id, "ice", candidate);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not start call.");
      await finish();
    } finally {
      setBusy(false);
      lock.current = false;
    }
  }
  async function answer() {
    const next = current.current;
    if (!next || lock.current) return;
    lock.current = true;
    setBusy(true);
    setMessage("");
    try {
      const pc = await makePeer();
      await pc.setRemoteDescription(next.offer);
      await pc.setLocalDescription(await pc.createAnswer());
      await signal(next.id, "answer", pc.localDescription?.toJSON());
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not answer call.");
      await finish();
    } finally {
      setBusy(false);
      lock.current = false;
    }
  }
  useEffect(() => {
    if (!client || !user) return;
    let alive = true,
      fetching = false;
    let lastSuccess = Date.now();
    const poll = async () => {
      if (fetching) return;
      fetching = true;
      try {
        const r = await client.rpc("taxi_call_state", { call_id: current.current?.id ?? null });
        if (!alive) return;
        if (r.error) {
          if (current.current && Date.now() - lastSuccess > 45000) {
            await finish();
            setMessage("Call stopped because the connection was lost.");
          }
          return;
        }
        lastSuccess = Date.now();
        const next = r.data as Call | null;
        if (!next?.id) return;
        if (!["ringing", "answered"].includes(next.status)) {
          current.current = null;
          setCall(null);
          cleanup();
          setMessage(
            next.status === "declined"
              ? "Call declined."
              : next.status === "missed"
                ? "No answer or connection lost."
                : "Call ended.",
          );
          return;
        }
        // A refreshed caller page cannot restore microphone/peer state.
        if (next.caller === user.id && !peer.current) {
          await signal(next.id, "end");
          return;
        }
        current.current = next;
        setCall(next);
        const pc = peer.current;
        if (pc && next.answer && !pc.remoteDescription && next.caller === user.id)
          await pc.setRemoteDescription(next.answer);
        if (pc?.remoteDescription) {
          const candidates = next.caller === user.id ? next.callee_ice : next.caller_ice;
          while (seenIce.current < candidates.length) {
            await pc.addIceCandidate(candidates[seenIce.current]);
            seenIce.current++;
          }
        }
      } catch {
        if (alive && current.current) setMessage("Connection interrupted. Reconnecting…");
      } finally {
        fetching = false;
      }
    };
    void poll();
    const interval = window.setInterval(() => void poll(), 1500);
    return () => {
      alive = false;
      window.clearInterval(interval);
      const id = current.current?.id;
      current.current = null;
      cleanup();
      setCall(null);
      if (id) void signal(id, "end").catch(() => {});
    };
  }, [client, user, cleanup, signal, finish]);
  const incoming = call && call.caller !== user?.id && call.status === "ringing";
  return (
    <VoiceContext.Provider
      value={{ start: (id, kind) => void start(id, kind), busy: busy || !!call }}
    >
      {children}
      <audio ref={audio} autoPlay />
      {call && (
        <RecordFormDialog
          open
          hideTrigger
          title={incoming ? "Incoming voice call" : "Voice call"}
          onClose={() => void finish(incoming ? "decline" : "end")}
        >
          <div className="form-grid">
            <h3>{call.peer_name}</h3>
            <p role="status">
              {connected
                ? "Connected"
                : incoming
                  ? "Would you like to answer?"
                  : call.status === "ringing"
                    ? "Calling…"
                    : "Connecting audio…"}
            </p>
            {incoming && (
              <button className="button" disabled={busy} onClick={() => void answer()}>
                Answer call
              </button>
            )}
            {!incoming && (
              <button
                className="button button--secondary"
                disabled={busy}
                onClick={() => {
                  stream.current?.getAudioTracks().forEach((t) => {
                    t.enabled = muted;
                  });
                  setMuted(!muted);
                }}
              >
                {muted ? "Unmute microphone" : "Mute microphone"}
              </button>
            )}
            {audioBlocked && (
              <button
                className="button"
                onClick={() => {
                  void audio.current?.play().then(() => setAudioBlocked(false));
                }}
              >
                Enable call audio
              </button>
            )}
            <button
              className="button button--danger"
              onClick={() => void finish(incoming ? "decline" : "end")}
            >
              {incoming ? "Decline call" : "End call"}
            </button>
            {message && <p role="status">{message}</p>}
          </div>
        </RecordFormDialog>
      )}
      {!call && (message || busy) && (
        <aside className="taxi-call-notice" role="status">
          {busy ? "Opening microphone…" : message}
          {busy && (
            <button type="button" onClick={() => void finish()}>
              Cancel
            </button>
          )}
          {!busy && (
            <button type="button" aria-label="Dismiss call message" onClick={() => setMessage("")}>
              ×
            </button>
          )}
        </aside>
      )}
    </VoiceContext.Provider>
  );
}
